export interface FilterOption {
  value: string;
  label: string;
}

interface FilterChipGroupProps {
  label: string;
  options: FilterOption[];
  selected: string[];
  onSelect: (value: string) => void;
  showLabel?: boolean;
}

export function FilterChipGroup({ label, options, selected, onSelect, showLabel = true }: FilterChipGroupProps) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
      {showLabel && (
        <span className="mt-1.5 w-16 shrink-0 text-xs text-foreground-400 sm:text-[13px]">{label}</span>
      )}
      <div className="flex flex-wrap gap-1.5">
        {options.map((option) => {
          const active = selected.includes(option.value);
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onSelect(option.value)}
              aria-pressed={active}
              className={[
                "cursor-pointer whitespace-nowrap rounded-full border px-3 py-1.5 text-xs transition-colors",
                active
                  ? "border-primary-500 bg-primary-500 text-background-50"
                  : "border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-600",
              ].join(" ")}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface SortTabsProps {
  options: FilterOption[];
  value: string;
  onChange: (value: string) => void;
}

export function SortTabs({ options, value, onChange }: SortTabsProps) {
  return (
    <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={[
            "cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors",
            value === option.value
              ? "bg-foreground-950 text-background-50"
              : "text-foreground-600 hover:text-primary-600",
          ].join(" ")}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-xs text-foreground-500">
      <span className="whitespace-nowrap">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-xs text-foreground-800 focus:border-primary-400 focus:outline-none"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}